import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { getUserDetails } from "../services/authService";
import { useAppContext } from "../contexts/AppContext";
import { PRODUCT_IDS } from "../i18n/productContext";
import { ROUTE_IDS } from "../i18n/routeIds";
import { resolveRoutePath } from "../i18n/routeGuards";
import { resolveProductFromPathname } from "../i18n/routeLocale";
import { LoaderModal } from "../Components/Loader/LoaderModal";
import NotFound from "../Pages/NotFound/NotFound";

/**
 * Auth gate for locale-prefixed agent dashboards (/en/dashboard/*, /el/dashboard/*).
 */
export function SecureAgentDashboardGate({ children }) {
  const navigate = useNavigate();
  const { isAppLoggedInValue } = useAppContext();

  const readSessionUser = () => getUserDetails();
  const hasPersistedSession = (u) =>
    !!(u && typeof u === "object" && (u.sessionID || u.sessionId));

  const [authenticated, setAuthenticated] = useState(() =>
    hasPersistedSession(readSessionUser())
  );
  const [authenticatedUser, setAuthenticatedUser] = useState(() => {
    const u = readSessionUser();
    return hasPersistedSession(u) ? u : null;
  });

  const lastSecureGateKeyRef = useRef("");

  useEffect(() => {
    const u = readSessionUser();
    const ctxLoggedIn = Boolean(isAppLoggedInValue);
    const sid =
      u && typeof u === "object"
        ? String(u.sessionID || u.sessionId || "").trim()
        : "";
    const ut =
      u && typeof u === "object" ? String(u.usertype || u.userType || "").trim() : "";
    const gateKey = `${ctxLoggedIn}|${hasPersistedSession(u)}|${sid}|${ut}`;
    if (lastSecureGateKeyRef.current === gateKey) {
      return;
    }
    lastSecureGateKeyRef.current = gateKey;

    if (hasPersistedSession(u) || ctxLoggedIn) {
      setAuthenticated(true);
      if (u && typeof u === "object") setAuthenticatedUser(u);
      return;
    }

    const { locale } = resolveProductFromPathname(window.location.pathname);
    const loginPath =
      resolveRoutePath(ROUTE_IDS.LOGIN, {
        locale,
        product: PRODUCT_IDS.PUBLIC,
      })?.path || "/login";
    navigate(loginPath, { replace: true });
  }, [isAppLoggedInValue, navigate]);

  useEffect(() => {
    if (!authenticated) return;
    const u = readSessionUser();
    if (hasPersistedSession(u)) setAuthenticatedUser(u);
  }, [authenticated]);

  if (!authenticated) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoaderModal />
      </div>
    );
  }

  if (
    !authenticatedUser ||
    typeof authenticatedUser !== "object" ||
    !authenticatedUser.usertype
  ) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoaderModal />
      </div>
    );
  }

  const userType = String(authenticatedUser.usertype || "").toLowerCase();
  if (userType === "agents" || userType === "agent" || userType === "admins" || userType === "admin") {
    return children;
  }

  return (
    <Routes>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default SecureAgentDashboardGate;
